import { useEffect, useRef } from "react";
import { ChevronRightIcon, FileIcon, ImageIcon, ListChecksIcon, SlashIcon, TerminalIcon, ZapIcon } from "../icons";
import type { CommandDeclaration, ResourceSummary } from "../types";

/**
 * The composer's + menu: attach photos, videos or files, mention a task, and run the profile's
 * workflows. A command runs or opens its dialog; a workflow with no command is filled into the
 * input as `/name `.
 */
export function PlusMenu(props: {
	commands: CommandDeclaration[];
	workflows: ResourceSummary[];
	/** Absent where the agent cannot read tasks. */
	onMentionTask?: () => void;
	onPickMedia: () => void;
	onPickFile: () => void;
	onCommand: (cmd: CommandDeclaration) => void;
	opensDialog: (cmd: CommandDeclaration) => boolean;
	disabled: (cmd: CommandDeclaration) => boolean;
	onFill: (text: string) => void;
	onClose: () => void;
}) {
	const menu = useRef<HTMLDivElement>(null);
	const close = useRef(props.onClose);
	close.current = props.onClose;
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => e.key === "Escape" && close.current();
		addEventListener("keydown", onKey);
		menu.current?.querySelector<HTMLButtonElement>("button")?.focus();
		return () => removeEventListener("keydown", onKey);
	}, []);

	const covered = new Set(props.commands.map((c) => c.workflow).filter(Boolean));
	const loose = props.workflows.filter((w) => !covered.has(w.name));
	const pick = (run: () => void) => () => {
		props.onClose();
		run();
	};

	return (
		<>
			<div className="plus-scrim" onClick={props.onClose} />
			<div className="plus-menu" ref={menu} role="menu" aria-label="上传与 workflow">
				<div className="plus-actions">
					<button role="menuitem" onClick={pick(props.onPickMedia)}>
						<span className="plus-glyph tint-blue">
							<ImageIcon size={22} />
						</span>
						照片和视频
					</button>
					<button role="menuitem" onClick={pick(props.onPickFile)}>
						<span className="plus-glyph tint-orange">
							<FileIcon size={22} />
						</span>
						文件
					</button>
					{props.onMentionTask && (
						<button role="menuitem" onClick={pick(props.onMentionTask)}>
							<span className="plus-glyph tint-violet">
								<ListChecksIcon size={22} />
							</span>
							引用任务
						</button>
					)}
				</div>
				{(props.commands.length > 0 || loose.length > 0) && (
					<>
						<span className="plus-label">Workflow</span>
						<div className="plus-list">
							{props.commands.map((cmd) => (
								<button
									key={cmd.name}
									role="menuitem"
									className="plus-row"
									title={cmd.workflow ? `/${cmd.workflow}` : cmd.script}
									disabled={props.disabled(cmd)}
									onClick={pick(() => props.onCommand(cmd))}
								>
									<span className="plus-row-glyph">{cmd.workflow ? <ZapIcon size={18} /> : <TerminalIcon size={18} />}</span>
									<span className="plus-row-name">{cmd.label}</span>
									<span className="plus-row-slug">{cmd.workflow ? `/${cmd.workflow}` : cmd.script}</span>
									{props.opensDialog(cmd) && <ChevronRightIcon size={16} className="plus-row-chev" />}
								</button>
							))}
							{loose.map((w) => (
								<button key={w.name} role="menuitem" className="plus-row" title={w.description || `/${w.name}`} onClick={pick(() => props.onFill(`/${w.name} `))}>
									<span className="plus-row-glyph plain">
										<SlashIcon size={18} />
									</span>
									<span className="plus-row-name">{w.name}</span>
									<span className="plus-row-slug">/{w.name}</span>
									<span className="plus-fill">填入</span>
								</button>
							))}
						</div>
					</>
				)}
			</div>
		</>
	);
}
